/**
 * Composable for Referral Token Handler
 * Обработка реферального токена из URL и сохранение для последующего применения
 * 
 * Follows DRY, SOLID principles and Vue.js Style Guide
 */

import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { consola } from 'consola';
import { useReferral } from './useReferral';

const logger = consola.withTag('referral-token-handler');

// Константы для localStorage ключей
const STORAGE_KEY = 'pendingReferralToken';
const STORAGE_TIMESTAMP_KEY = 'pendingReferralTokenTimestamp';

/**
 * Composable для обработки реферального токена
 * 
 * Функциональность:
 * - Извлечение токена из URL параметра ?ref=
 * - Сохранение токена в localStorage
 * - Валидация токена (опционально)
 * - Получение сохраненного токена
 * - Очистка токена после применения
 */
export const useReferralTokenHandler = () => {
  const route = useRoute();
  const router = useRouter();
  const { validateToken } = useReferral();

  // State
  const pendingToken = ref<string | null>(null);
  const isValidating = ref(false);
  const validationError = ref<string | null>(null);

  // ============================================
  // Computed
  // ============================================

  /**
   * Проверка наличия сохраненного токена
   */
  const hasPendingToken = computed(() => {
    if (pendingToken.value) return true;
    if (process.client) {
      return !!localStorage.getItem(STORAGE_KEY);
    }
    return false;
  });

  /**
   * Получение токена из URL
   */
  const getTokenFromUrl = (): string | null => {
    if (!process.client) return null;
    
    const token = route.query.ref as string | undefined;
    if (token && typeof token === 'string' && token.trim().length > 0) {
      return token.trim();
    }
    return null;
  };

  // ============================================
  // Methods
  // ============================================

  /**
   * Загрузка токена из localStorage
   */
  const loadTokenFromStorage = (): string | null => {
    if (!process.client) return null;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        pendingToken.value = stored;
        if (process.dev) {
          logger.debug('Токен загружен из localStorage:', stored);
        }
        return stored;
      }
    } catch (error) {
      logger.error('Ошибка загрузки токена из localStorage:', error);
    }
    return null;
  };

  /**
   * Сохранение токена в localStorage
   */
  const saveTokenToStorage = (token: string): void => {
    if (!process.client) return;

    try {
      localStorage.setItem(STORAGE_KEY, token);
      localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
      pendingToken.value = token;
      
      if (process.dev) {
        logger.debug('Токен сохранен в localStorage:', token);
      }
    } catch (error) {
      logger.error('Ошибка сохранения токена в localStorage:', error);
    }
  };

  /**
   * Очистка токена из localStorage
   */
  const clearToken = (): void => {
    if (!process.client) return;

    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      pendingToken.value = null;
      validationError.value = null;
      
      if (process.dev) {
        logger.debug('Токен очищен из localStorage');
      }
    } catch (error) {
      logger.error('Ошибка очистки токена из localStorage:', error);
    }
  };

  /**
   * Получение сохраненного токена
   */
  const getPendingToken = (): string | null => {
    if (pendingToken.value) {
      return pendingToken.value;
    }
    return loadTokenFromStorage();
  };

  /**
   * Валидация токена на бэкенде
   */
  const validatePendingToken = async (token?: string): Promise<boolean> => {
    const tokenToValidate = token || getPendingToken();
    if (!tokenToValidate) {
      validationError.value = 'Токен не найден';
      return false;
    }

    isValidating.value = true;
    validationError.value = null;

    try {
      if (process.dev) {
        logger.debug('Валидация токена:', tokenToValidate);
      }

      const isValid = await validateToken(tokenToValidate);
      
      if (isValid) {
        if (process.dev) {
          logger.success('Токен валиден');
        }
        isValidating.value = false;
        return true;
      } else {
        validationError.value = 'Токен недействителен';
        logger.warn('Токен не прошел валидацию');
        isValidating.value = false;
        return false;
      }
    } catch (error: any) {
      validationError.value = error?.data?.message || 'Ошибка валидации токена';
      logger.error('Ошибка валидации токена:', error);
      isValidating.value = false;
      return false;
    }
  };

  /**
   * Обработка токена из URL
   * Извлекает токен из ?ref= и сохраняет в localStorage
   */
  const handleTokenFromUrl = (options?: {
    validate?: boolean;
    clearUrl?: boolean;
  }): string | null => {
    if (!process.client) return null;

    const token = getTokenFromUrl();
    if (!token) {
      return null;
    }

    // Сохраняем токен
    saveTokenToStorage(token);

    // Валидация (опционально)
    if (options?.validate) {
      void validatePendingToken(token);
    }

    // Очистка URL от параметра (опционально)
    if (options?.clearUrl) {
      const newQuery = { ...route.query };
      delete newQuery.ref;
      
      router.replace({
        path: route.path,
        query: newQuery,
      });
      
      if (process.dev) {
        logger.debug('Параметр ?ref= удален из URL');
      }
    }

    if (process.dev) {
      logger.info('Реферальный токен обработан:', token);
    }

    return token;
  };

  /**
   * Инициализация - загрузка токена из localStorage при монтировании
   */
  const init = (): void => {
    loadTokenFromStorage();
  };

  // Инициализация при создании composable
  if (process.client) {
    init();
  }

  return {
    // State
    pendingToken: computed(() => pendingToken.value),
    isValidating: computed(() => isValidating.value),
    validationError: computed(() => validationError.value),
    hasPendingToken,

    // Methods
    getTokenFromUrl,
    getPendingToken,
    saveTokenToStorage,
    loadTokenFromStorage,
    clearToken,
    validatePendingToken,
    handleTokenFromUrl,
    init,
  };
};

